import type React from 'react';
import {
  LIFE_REBUILD_REPORT_PRICE_USD,
  LIFE_REBUILD_REPORT_TASK_ID,
} from './assessments/life-rebuild-score/report-types';
import {
  PROBLEM_FINDER_PLAN_PRICE_USD,
  PROBLEM_FINDER_PLAN_PRODUCT,
  PROBLEM_FINDER_PLAN_TASK_ID,
} from './assessments/problem-finder/plan-types';
import { PROBLEMS } from './assessments/problem-finder/problems';
import { ProblemFinderAnalysisResponseSchema } from './assessments/problem-finder/types';

// The one-off paid reports sold after a free assessment. Checkout, claim, the report
// page and the generator task all look up the product here by assessment id, so a new
// paid report is one entry, not another copy of the Life Rebuild path.

export type ReportPricing =
  /** A Stripe price whose id lives in this env var. */
  | { kind: 'stripe-price'; priceIdEnv: string }
  /** No Stripe price: checkout builds the line item from priceUsd and this name. */
  | { kind: 'inline'; lineItemName: (analysisResult: unknown) => string };

export interface PaidReportProduct {
  assessmentId: string;
  /** Stripe metadata `product`, and the `product` property on every report event. */
  product: string;
  /** What the buyer calls it, after "your": "your Life Rebuild Report", "your 14-day plan". */
  itemName: string;
  /** Trigger.dev task that writes the report once payment is verified. */
  taskId: string;
  priceUsd: number;
  pricing: ReportPricing;
  /**
   * Questions asked after payment, before the report is written. The generator is
   * scheduled this long after the claim and pulled forward when the reader answers, so
   * a reader who leaves still gets the report.
   */
  intake: { waitFor: string } | null;
}

export const PAID_REPORTS = {
  'life-rebuild-score': {
    assessmentId: 'life-rebuild-score',
    product: 'life-rebuild-report',
    itemName: 'Life Rebuild Report',
    taskId: LIFE_REBUILD_REPORT_TASK_ID,
    priceUsd: LIFE_REBUILD_REPORT_PRICE_USD,
    pricing: { kind: 'stripe-price', priceIdEnv: 'STRIPE_PRICE_ID_LIFE_REBUILD_REPORT' },
    intake: null,
  },
  'problem-finder': {
    assessmentId: 'problem-finder',
    product: PROBLEM_FINDER_PLAN_PRODUCT,
    itemName: '14-day plan',
    taskId: PROBLEM_FINDER_PLAN_TASK_ID,
    priceUsd: PROBLEM_FINDER_PLAN_PRICE_USD,
    pricing: {
      kind: 'inline',
      lineItemName: (analysisResult: unknown) => {
        const parsed = ProblemFinderAnalysisResponseSchema.safeParse(analysisResult);
        if (!parsed.success) {
          throw new Error('Problem Finder checkout needs a completed analysis');
        }
        return `Your ${PROBLEMS[parsed.data.problemId].planName}`;
      },
    },
    intake: { waitFor: '15m' },
  },
} as const satisfies Record<string, PaidReportProduct>;

export type PaidReportAssessmentId = keyof typeof PAID_REPORTS;
/** A registry entry, with its assessment id narrowed to the literal. */
export type PaidReport = (typeof PAID_REPORTS)[PaidReportAssessmentId];

export function isPaidReportAssessmentId(value: unknown): value is PaidReportAssessmentId {
  return typeof value === 'string' && Object.prototype.hasOwnProperty.call(PAID_REPORTS, value);
}

export function getPaidReport(assessmentId: unknown): PaidReport | null {
  return isPaidReportAssessmentId(assessmentId) ? PAID_REPORTS[assessmentId] : null;
}

/** The paid report a Stripe `product` metadata value or email context belongs to. */
export function findPaidReportByProduct(product: unknown): PaidReport | null {
  const reports: PaidReport[] = Object.values(PAID_REPORTS);
  return reports.find((report) => report.product === product) ?? null;
}

export type VisitorReportStatus = 'pending' | 'generating' | 'ready' | 'failed';

/** What the reader told us after paying. Answers are keyed by intake question id. */
export interface ReportIntake {
  answers: Record<string, string>;
  skipped: boolean;
  submittedAt: unknown;
}

// Firestore `visitor_reports/{runId}` document. `runId` is the assessment run id
// (same key as `visitor_assessments/{runId}`). `accessToken` gates the report URL.
export interface VisitorReportDoc<TReport = unknown> {
  runId: string;
  assessmentId: PaidReportAssessmentId;
  email: string;
  name: string;
  accessToken: string;
  stripeSessionId: string;
  amountTotalCents: number | null;
  paid: true;
  status: VisitorReportStatus;
  reportTaskRunId: string | null;
  report: TReport | null;
  error: string | null;
  /** Absent on reports claimed before intake existed, and on reports without one. */
  intake?: ReportIntake | null;
  createdAt: unknown;
  updatedAt: unknown;
}

// Public shape returned by GET /api/report/[runId]?t=<accessToken>.
export interface VisitorReportResponse<TReport = unknown> {
  runId: string;
  /** Lets a report page refuse a report that another assessment sold. */
  assessmentId: PaidReportAssessmentId;
  name: string;
  status: VisitorReportStatus;
  report: TReport | null;
  error: string | null;
  /** ISO time of purchase (the claim). Gates the in-report app offer. */
  purchasedAt: string | null;
  /** The page should ask the intake questions before showing the writing state. */
  needsIntake: boolean;
}

/** True while a paid report with intake questions is still waiting for the answers. */
export function reportNeedsIntake(doc: Pick<VisitorReportDoc, 'assessmentId' | 'status' | 'intake'>): boolean {
  return getPaidReport(doc.assessmentId)?.intake != null && doc.status === 'pending' && !doc.intake;
}

// Injected by the app (minitools) into result pages so this package stays free of Stripe.
export interface ReportCheckoutButtonProps {
  runId: string;
  email?: string;
  assessmentType: PaidReportAssessmentId;
  ctaLocation: string;
  className?: string;
  children: React.ReactNode;
}
